/**
 * Account Setting Platform-List Helpers (frontend mirror)
 *
 * Generic helpers for account settings that store a comma-separated list of
 * platforms (e.g., 'launch-template-required', 'enable-mmgy-io-type').
 *
 * Contract (going forward):
 *   - The setting value is EITHER a comma-separated platform list (e.g.,
 *     'dv360,ttd') OR the setting key is absent (null / undefined / '').
 *   - Legacy boolean values ('true' / 'false') are no longer used and are
 *     not given any special interpretation. If a stray boolean string is
 *     encountered, it will be treated as a literal one-element list (which
 *     won't match any real platform).
 *
 * Mirrors apps/app-backend/utils/accountSettingPlatformList.js — keep in sync.
 */

/**
 * Platform alias -> canonical platform name.
 *
 * These settings are typed by hand in the admin UI and compared against
 * platform/destination identifiers that come from several conventions, so the
 * same platform legitimately appears as 'g-ads-search' (import convention),
 * 'google_search' (legacy), 'search' (shorthand) or 'google-ads-search'
 * (destination id). Canonicalizing BOTH the setting entries and the platform
 * being checked makes every spelling interchangeable.
 *
 * Purely additive: an exact match that works today still matches, because
 * both sides go through the same mapping.
 */
const PLATFORM_ALIASES = {
  'universal_ads': 'universal-ads', 'universalads': 'universal-ads', 'comcast': 'universal-ads',
  // Google Ads — Search
  'google_search': 'google-ads-search',
  'g-ads-search': 'google-ads-search',
  'gads-search': 'google-ads-search',
  'search': 'google-ads-search',
  // Google Ads — Performance Max
  'google_pmax': 'google-ads-pmax',
  'g-ads-pmax': 'google-ads-pmax',
  'gads-pmax': 'google-ads-pmax',
  'pmax': 'google-ads-pmax',
  // Google Ads — Demand Gen
  'google_demandgen': 'google-ads-demandgen',
  'g-ads-demandgen': 'google-ads-demandgen',
  'gads-demandgen': 'google-ads-demandgen',
  'demandgen': 'google-ads-demandgen',
  'demand-gen': 'google-ads-demandgen',
  'demand_gen': 'google-ads-demandgen',
  // Google Ads — Display
  'google_display': 'google-ads-display',
  'g-ads-display': 'google-ads-display',
  'gads-display': 'google-ads-display',
  // YouTube / DV360
  'youtube_dv360': 'youtube-dv360',
  'dv360-youtube': 'youtube-dv360',
  'dv360_youtube': 'youtube-dv360',
  // Meta
  'fb': 'facebook',
  'meta': 'facebook',
  // Amazon
  'amazon_dsp': 'amazon-dsp',
  'amazondsp': 'amazon-dsp',
  // Yahoo
  'yahoo_dsp': 'yahoo',
  'yahoo-dsp': 'yahoo',
  'yahoodsp': 'yahoo',
  // Google Ad Manager
  'google-ad-manager': 'gam',
  'google_ad_manager': 'gam',
};

/**
 * Resolve a platform name (any known alias) to its canonical form.
 * Unknown values pass through trimmed + lowercased, exactly as before.
 *
 * @param {string|null|undefined} platform
 * @returns {string}
 */
export function canonicalPlatform(platform) {
  if (!platform) return '';
  const normalized = String(platform).trim().toLowerCase();
  return PLATFORM_ALIASES[normalized] || normalized;
}

/**
 * Returns true if the setting is enabled for the given platform.
 *
 * @param {string|null|undefined} settingValue
 * @param {string|null|undefined} platform
 * @returns {boolean}
 */
export function isEnabledForPlatform(settingValue, platform) {
  if (!settingValue) return false;
  const target = canonicalPlatform(platform);
  if (!target) return false;
  return getEnabledPlatforms(settingValue).includes(target);
}

/**
 * Returns true if the setting is enabled for AT LEAST ONE platform.
 * Use for account-level UI gating where the per-context platform isn't known.
 *
 * @param {string|null|undefined} settingValue
 * @returns {boolean}
 */
export function isEnabledForAnyPlatform(settingValue) {
  if (!settingValue) return false;
  return String(settingValue)
    .split(',')
    .some((p) => p.trim().length > 0);
}

/**
 * Returns true if the setting is enabled for AT LEAST ONE platform in the given list.
 * Useful for multi-channel campaign UI: "show this control if any selected
 * destination has the setting enabled".
 *
 * @param {string|null|undefined} settingValue
 * @param {string[]} platforms
 * @returns {boolean}
 */
export function isEnabledForAnyOfPlatforms(settingValue, platforms) {
  if (!settingValue) return false;
  if (!Array.isArray(platforms) || platforms.length === 0) return false;
  return platforms.some((p) => isEnabledForPlatform(settingValue, p));
}

/**
 * Returns the explicit list of platforms enabled by the setting, canonicalized
 * (see canonicalPlatform) and deduplicated.
 * - For empty / null / undefined, returns [].
 * - Entries are compared against canonical destination/channel ids, so a
 *   setting written as 'g-ads-search,g-ads-pmax' yields
 *   ['google-ads-search', 'google-ads-pmax'].
 *
 * @param {string|null|undefined} settingValue
 * @returns {string[]}
 */
export function getEnabledPlatforms(settingValue) {
  if (!settingValue) return [];
  const platforms = String(settingValue)
    .split(',')
    .map(canonicalPlatform)
    .filter(Boolean);
  return [...new Set(platforms)];
}
